import { BASE_API_URL, fetchWithAuth } from "./api";

/** Campo adicional que un cliente pide en su formulario. */
export interface CampoExtra {
  clave: string;
  etiqueta: string;
  tipo: "texto" | "opciones";
  obligatorio?: boolean;
  opciones?: string[];
}

export interface TipoEvaluacion {
  clave: string;
  nombre: string;
  descripcion: string | null;
}

export interface Cliente {
  id: number;
  nombre: string;
  slug: string;
  url_publica: string;
  campaign_id: number | null;
  tipo_evaluacion: string | null;
  logo_url: string | null;
  logo_secundario_url: string | null;
  color_principal: string | null;
  titulo: string | null;
  descripcion: string | null;
  consentimiento: string | null;
  email_notificaciones: string | null;
  activo: boolean;
  evaluaciones: number;
  completadas: number;
}

/** Datos de marca para la landing pública de un cliente. */
export interface ClientePublico {
  slug: string;
  nombre: string;
  consentimiento: string;
  campos_extra?: CampoExtra[];
  /** Campos estándar que este cliente no pide. */
  campos_ocultos?: string[];
  logo_url: string | null;
  logo_secundario_url: string | null;
  color_principal: string | null;
  titulo: string | null;
  descripcion: string | null;
}

export type ClienteInput = Partial<
  Pick<
    Cliente,
    | "nombre"
    | "slug"
    | "campaign_id"
    | "tipo_evaluacion"
    | "logo_url"
    | "logo_secundario_url"
    | "color_principal"
    | "titulo"
    | "descripcion"
    | "consentimiento"
    | "email_notificaciones"
    | "activo"
  >
>;

export const clienteService = {
  async listar(): Promise<Cliente[]> {
    const body = await fetchWithAuth("/clientes");

    if (!body?.success) {
      throw new Error(body?.message || "No se pudieron cargar los clientes.");
    }

    return (body.data ?? []) as Cliente[];
  },

  /** Tipos de evaluación disponibles para elegir al crear un cliente. */
  async tiposEvaluacion(): Promise<TipoEvaluacion[]> {
    const body = await fetchWithAuth("/clientes/tipos-evaluacion");

    if (!body?.success) {
      throw new Error(body?.message || "No se pudieron cargar los tipos.");
    }

    return (body.data ?? []) as TipoEvaluacion[];
  },

  async obtener(id: number): Promise<Cliente> {
    const body = await fetchWithAuth(`/clientes/${id}`);

    if (!body?.success) {
      throw new Error(body?.message || "No se pudo cargar el cliente.");
    }

    return body.data as Cliente;
  },

  async crear(input: ClienteInput): Promise<Cliente> {
    const body = await fetchWithAuth("/clientes", {
      method: "POST",
      body: JSON.stringify(input),
    });

    if (!body?.success) {
      throw new Error(body?.message || "No se pudo crear el cliente.");
    }

    return body.data as Cliente;
  },

  async actualizar(id: number, input: ClienteInput): Promise<Cliente> {
    const body = await fetchWithAuth(`/clientes/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });

    if (!body?.success) {
      throw new Error(body?.message || "No se pudo actualizar el cliente.");
    }

    return body.data as Cliente;
  },

  /** Marca del cliente para su landing pública. No requiere sesión. */
  async porSlug(slug: string): Promise<ClientePublico> {
    const response = await fetch(`${BASE_API_URL}/clientes/por-slug/${slug}`, {
      headers: { Accept: "application/json" },
    });

    const body = await response.json().catch(() => null);

    if (!response.ok || !body?.success) {
      throw new Error(body?.message || "Esta evaluación no está disponible.");
    }

    return body.data as ClientePublico;
  },
};
